import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { devtools } from "../../services/devtools";
import { requestCache } from "../../services/requestCache";
import { montarArvore, rotuloDaChave } from "./arvore";

/**
 * Barra de diagnóstico — o que o app faz por baixo, enquanto você usa o app.
 *
 * Docada no rodapé como o Laravel Debugbar: abas por canal com contador,
 * leitura resumida à direita, e o corpo abrindo por cima. Clicar numa linha
 * abre o inspetor com o conteúdo daquele evento.
 *
 * As abas saem dos CANAIS que registraram evento (services/devtools.js) — não
 * há lista fixa aqui. Instrumentar um lugar novo (`registrar("http", ...)`)
 * faz a aba aparecer sozinha.
 *
 * ── Decisões de desenho ──────────────────────────────────────────────────
 *
 * Estilo inline, sem Material-UI e sem folha de estilo: isolamento nos dois
 * sentidos. A barra observa o app, então não pode ser afetada pelo tema dele
 * (se o defeito for no tema, ela precisa continuar legível) nem vazar estilo
 * para ele.
 *
 * Monospace porque o conteúdo é colunar — horário, chave, contadores — e o
 * alinhamento por caractere é o que deixa varrer a lista com o olho. Números
 * tabulares pelo mesmo motivo: contador que muda não pode fazer a linha
 * dançar.
 *
 * A faixa de pulso é a única coisa que se move sozinha, e é o ponto: ela
 * responde "está acontecendo alguma coisa agora?" sem precisar ler nada.
 */

const ATALHO = { ctrl: true, shift: true, tecla: "d" };
const ALTURA_MIN = 140;
const FRACAO_MAX = 0.75;
const JANELA_PULSO = 40; // segundos mostrados na faixa

/** Uma cor por ESTADO, não por enfeite: a cor é o que separa custo de economia. */
const TOM = {
  hit: "#5BC8AC", // veio da memória
  compartilhada: "#7FA9E8", // aproveitou chamada em voo
  miss: "#E8A33D", // foi à rede — o custo
  invalidacao: "#E2685E", // cache derrubado
  recebido: "#B48EE8", // evento chegou
  conectou: "#5BC8AC",
  caiu: "#E2685E",
  guardado: "#7FA9E8"
};

const ROTULO = {
  hit: "memória",
  compartilhada: "juntou",
  miss: "rede",
  invalidacao: "derrubou",
  recebido: "chegou",
  conectou: "conectou",
  caiu: "caiu",
  guardado: "guardado"
};

const NOME_CANAL = { cache: "Cache", socket: "Socket" };

const doisDigitos = n => String(n).padStart(2, "0");

const hora = ms => {
  const d = new Date(ms);
  return `${doisDigitos(d.getHours())}:${doisDigitos(d.getMinutes())}:${doisDigitos(
    d.getSeconds()
  )}.${String(d.getMilliseconds()).padStart(3, "0")}`;
};

const Devtools = () => {
  const [ligado, setLigado] = useState(devtools.estaLigado());
  const [, forcar] = useState(0);
  const [aba, setAba] = useState(null);
  const [selecionado, setSelecionado] = useState(null);
  const [ampliado, setAmpliado] = useState(null); // payload aberto em tela cheia
  const [altura, setAltura] = useState(280);
  const arrastando = useRef(false);

  // O atalho vale mesmo desligado — é o único jeito de ligar sem o console.
  useEffect(() => {
    const aoTeclar = e => {
      if (
        e.ctrlKey === ATALHO.ctrl &&
        e.shiftKey === ATALHO.shift &&
        String(e.key).toLowerCase() === ATALHO.tecla
      ) {
        e.preventDefault();
        setLigado(devtools.alternar());
        return;
      }
      if (e.key === "Escape") {
        // Fecha uma camada por vez: primeiro o payload ampliado, depois a
        // seleção. Esc que fecha tudo de uma vez faz perder o lugar na lista.
        setAmpliado(atual => {
          if (atual) return null;
          setSelecionado(null);
          return null;
        });
      }
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, []);

  // Só assina o fluxo quando ligado: desligado, nada re-renderiza.
  useEffect(() => {
    if (!ligado) return undefined;
    return devtools.assinar(() => forcar(n => n + 1));
  }, [ligado]);

  // O tempo até expirar precisa ANDAR, e nada dispara evento enquanto ele só
  // passa. Um tique por segundo, apenas com a aba de cache aberta.
  useEffect(() => {
    if (!ligado || aba !== "cache") return undefined;
    const t = setInterval(() => forcar(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [ligado, aba]);

  const aoArrastar = useCallback(e => {
    if (!arrastando.current) return;
    setAltura(
      Math.max(
        ALTURA_MIN,
        Math.min(window.innerHeight * FRACAO_MAX, window.innerHeight - e.clientY)
      )
    );
  }, []);

  useEffect(() => {
    const soltar = () => {
      arrastando.current = false;
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", aoArrastar);
    window.addEventListener("mouseup", soltar);
    return () => {
      window.removeEventListener("mousemove", aoArrastar);
      window.removeEventListener("mouseup", soltar);
    };
  }, [aoArrastar]);

  const canais = ligado ? devtools.canais() : [];
  const cache = ligado ? devtools.resumoCache() : null;
  const canalAberto = aba && canais.includes(aba) ? aba : null;
  const eventos = canalAberto ? devtools.resumo(canalAberto).eventos : [];
  const todos = ligado ? devtools.resumo().eventos : [];

  const pulso = useMemo(() => montarPulso(todos), [todos]);

  if (!ligado) return null;

  const abrirEmJanela = () => {
    // Mesma origem: a janela filha lê `opener` direto, sem postMessage.
    const j = window.open("", "mk-devtools", "width=640,height=720");
    if (!j) return;
    j.document.title = "Markedesk — instrumentos";
    j.document.body.style.cssText = `margin:0;background:${C.fundo};color:${C.texto}`;
    j.document.body.innerHTML = `<pre id="mk" style="font:12px/1.6 ${FONTE};padding:14px;margin:0"></pre>`;
    const pintar = () => {
      if (j.closed) return;
      const alvo = j.document.getElementById("mk");
      if (!alvo) return;
      const c = devtools.resumoCache();
      alvo.textContent = [
        `memória ${c.evitadas}    rede ${c.miss}    ${
          c.aproveitamento === null ? "" : c.aproveitamento + "% economizado"
        }`,
        "",
        ...devtools
          .resumo()
          .eventos.map(
            ev =>
              `${hora(ev.em)}  ${ev.canal.padEnd(7)} ${(ROTULO[ev.tipo] || ev.tipo).padEnd(
                9
              )} ${ev.chave}`
          )
      ].join("\n");
    };
    pintar();
    const cancelar = devtools.assinar(pintar);
    j.addEventListener("beforeunload", cancelar);
  };

  return (
    <aside style={est.raiz} aria-label="Instrumentos de diagnóstico">
      {canalAberto && (
        <>
          <div
            style={est.puxador}
            onMouseDown={() => {
              arrastando.current = true;
              document.body.style.userSelect = "none";
            }}
            title="Arraste para redimensionar"
          />
          <div style={{ ...est.corpo, height: altura }}>
            <div style={est.coluna}>
              {canalAberto === "cache" && (
                <ArvoreDeCaches
                  linhas={cache.porChave}
                  selecionado={selecionado}
                  aoSelecionar={k =>
                    setSelecionado(sel =>
                      sel?.chave === k.chave
                        ? null
                        : {
                            // Adaptado para o inspetor, que já sabe mostrar
                            // `chave` + `detalhe` — reaproveita o mesmo painel
                            // em vez de inventar um segundo.
                            id: `chave:${k.chave}`,
                            chave: k.chave,
                            canal: "cache",
                            tipo: "guardado",
                            em: Date.now(),
                            detalhe: {
                              memoria: k.hit,
                              juntou: k.compartilhada,
                              rede: k.miss,
                              expiraEm: k.restanteMs === null ? "não está em cache" : restante(k.restanteMs),
                              valor: k.valor
                            }
                          }
                    )
                  }
                />
              )}
              <Fluxo
                eventos={eventos}
                selecionado={selecionado}
                aoSelecionar={ev => setSelecionado(sel => (sel?.id === ev.id ? null : ev))}
              />
            </div>
            <Inspetor
              evento={selecionado}
              aoFechar={() => setSelecionado(null)}
              aoAmpliar={() => setAmpliado(selecionado)}
            />
          </div>
        </>
      )}

      <div style={est.barra}>
        <Pulso barras={pulso} />

        {canais.map(canal => {
          const ativa = canalAberto === canal;
          return (
            <button
              key={canal}
              type="button"
              onClick={() => {
                setAba(ativa ? null : canal);
                setSelecionado(null);
              }}
              style={{ ...est.aba, ...(ativa ? est.abaAtiva : null) }}
            >
              {NOME_CANAL[canal] || canal}
              <span style={est.contador}>{devtools.resumo(canal).total}</span>
            </button>
          );
        })}

        {canais.length === 0 && <span style={est.espera}>ouvindo</span>}

        <span style={{ flex: 1 }} />

        {cache && canais.includes("cache") && (
          <div style={est.leitura}>
            <Leitura valor={cache.evitadas} rotulo="memória" tom={TOM.hit} />
            <Leitura valor={cache.miss} rotulo="rede" tom={TOM.miss} />
            {cache.aproveitamento !== null && (
              <Leitura valor={`${cache.aproveitamento}%`} rotulo="economizado" />
            )}
          </div>
        )}

        <button type="button" onClick={abrirEmJanela} style={est.acao} title="Abrir em janela">
          &#9106;
        </button>
        <button
          type="button"
          onClick={() => {
            devtools.limpar();
            setSelecionado(null);
          }}
          style={est.acao}
          title="Limpar"
        >
          &#9003;
        </button>
        <button
          type="button"
          onClick={() => setLigado(devtools.alternar(false))}
          style={est.acao}
          title="Fechar — Ctrl+Shift+D"
        >
          &#10005;
        </button>
      </div>

      {ampliado && <Ampliado evento={ampliado} aoFechar={() => setAmpliado(null)} />}
    </aside>
  );
};

/** Barrinhas por segundo — a única coisa que se move sozinha, e é o ponto. */
const montarPulso = eventos => {
  const agora = Math.floor(Date.now() / 1000);
  const baldes = new Array(JANELA_PULSO).fill(null).map(() => ({ total: 0, tom: null }));
  eventos.forEach(ev => {
    const idade = agora - Math.floor(ev.em / 1000);
    if (idade < 0 || idade >= JANELA_PULSO) return;
    const b = baldes[JANELA_PULSO - 1 - idade];
    b.total += 1;
    // O balde assume o tom do evento mais "caro" que caiu nele: rede e queda
    // pesam mais que um acerto de memória — é o que a gente quer enxergar.
    const peso = { miss: 3, caiu: 3, invalidacao: 2 };
    if (!b.tom || (peso[ev.tipo] || 1) > (b.peso || 0)) {
      b.tom = TOM[ev.tipo] || C.suave;
      b.peso = peso[ev.tipo] || 1;
    }
  });
  const maior = Math.max(1, ...baldes.map(b => b.total));
  return baldes.map(b => ({ ...b, altura: b.total === 0 ? 0 : b.total / maior }));
};

const Pulso = ({ barras }) => (
  <div style={est.pulso} title="Eventos por segundo, últimos 40s" aria-hidden="true">
    {barras.map((b, i) => (
      <span
        key={i}
        style={{
          width: 3,
          height: `${Math.max(b.altura * 100, b.total ? 12 : 0)}%`,
          background: b.tom || "transparent",
          opacity: 0.9,
          alignSelf: "flex-end"
        }}
      />
    ))}
  </div>
);

const Leitura = ({ valor, rotulo, tom }) => (
  <span style={est.itemLeitura}>
    <strong style={{ color: tom || C.texto, fontVariantNumeric: "tabular-nums" }}>{valor}</strong>
    <span style={{ color: C.suave }}>{rotulo}</span>
  </span>
);

/** Segundos restantes, em forma curta: 42s, 4m12s. */
const restante = ms => {
  if (ms <= 0) return "vencido";
  const s = Math.ceil(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m${doisDigitos(s % 60)}s`;
};

const Contadores = ({ hit, compartilhada, miss }) => (
  <>
    <span style={{ color: TOM.hit }} title="veio da memória">
      {hit}
    </span>
    <span style={{ color: TOM.compartilhada }} title="aproveitou chamada em voo">
      {compartilhada}
    </span>
    <span style={{ color: TOM.miss }} title="foi à rede">
      {miss}
    </span>
  </>
);

/**
 * Árvore do que está em cache, direto no corpo do painel.
 *
 * Uma linha por DOMÍNIO porque a rolagem infinita de contatos cria uma chave
 * por página, e listar chave a chave virava ruído. Domínio abre e fecha ali
 * mesmo; clicar numa chave manda o detalhe para o inspetor ao lado — inclusive
 * o VALOR guardado, que responde "o cache está servindo o quê?", pergunta
 * diferente de "o cache pegou?".
 */
const ArvoreDeCaches = ({ linhas, selecionado, aoSelecionar }) => {
  const arvore = montarArvore(linhas);
  const [abertos, setAbertos] = useState(() => new Set());

  if (arvore.length === 0) return null;

  const alternar = nome =>
    setAbertos(atual => {
      const novo = new Set(atual);
      if (novo.has(nome)) novo.delete(nome);
      else novo.add(nome);
      return novo;
    });

  return (
    <div style={est.porChave}>
      {arvore.map(d => {
        const aberto = abertos.has(d.nome);
        return (
          <div key={d.nome}>
            <button type="button" onClick={() => alternar(d.nome)} style={est.noArvore}>
              <span style={est.seta}>{aberto ? "▾" : "▸"}</span>
              <span style={{ flex: 1, textAlign: "left" }}>{d.nome}</span>
              <Contadores hit={d.hit} compartilhada={d.compartilhada} miss={d.miss} />
              <span style={est.qtdChaves}>
                {d.chaves.length} {d.chaves.length === 1 ? "chave" : "chaves"}
              </span>
            </button>

            {aberto &&
              d.chaves.map(k => {
                const ativa = selecionado?.chave === k.chave;
                return (
                  <button
                    key={k.chave}
                    type="button"
                    onClick={() => aoSelecionar(k)}
                    style={{ ...est.folha, ...(ativa ? est.folhaAtiva : null) }}
                  >
                    <span style={{ flex: 1, textAlign: "left", wordBreak: "break-all" }}>
                      {rotuloDaChave(k.chave)}
                    </span>
                    <Contadores hit={k.hit} compartilhada={k.compartilhada} miss={k.miss} />
                    <span
                      style={{
                        ...est.validade,
                        color: k.restanteMs === null ? C.suave : C.texto
                      }}
                      title={k.restanteMs === null ? "não está mais em cache" : "tempo até expirar"}
                    >
                      {k.restanteMs === null ? "—" : restante(k.restanteMs)}
                    </span>
                  </button>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

const Fluxo = ({ eventos, selecionado, aoSelecionar }) => {
  if (eventos.length === 0) {
    return <p style={est.vazio}>Use o app — abra um ticket, transfira, liste tags.</p>;
  }
  return (
    <div style={est.fluxo}>
      {eventos.map(ev => {
        const ativo = selecionado?.id === ev.id;
        return (
          <button
            key={ev.id}
            type="button"
            onClick={() => aoSelecionar(ev)}
            style={{ ...est.linha, ...(ativo ? est.linhaAtiva : null) }}
          >
            <span style={est.horario}>{hora(ev.em)}</span>
            <span style={{ ...est.tipo, color: TOM[ev.tipo] || C.texto }}>
              {ROTULO[ev.tipo] || ev.tipo}
            </span>
            <span style={est.chave}>{ev.chave}</span>
            {ev.vezes > 1 && (
              <span style={est.vezes} title={`${ev.vezes} vezes seguidas`}>
                &times;{ev.vezes}
              </span>
            )}
            {ev.detalhe?.acao && <span style={est.dica}>{ev.detalhe.acao}</span>}
            {ev.detalhe?.motivo && <span style={est.dica}>{ev.detalhe.motivo}</span>}
          </button>
        );
      })}
    </div>
  );
};

const Inspetor = ({ evento, aoFechar, aoAmpliar }) => {
  const [copiado, setCopiado] = useState(false);

  useEffect(() => setCopiado(false), [evento]);

  if (!evento) {
    return (
      <div style={est.inspetor}>
        <p style={est.vazio}>Clique numa linha para ver o que veio dentro.</p>
      </div>
    );
  }

  const corpo = JSON.stringify(evento.detalhe ?? null, null, 2);

  const copiar = () => {
    try {
      navigator.clipboard.writeText(corpo);
      setCopiado(true);
    } catch {
      // Área de transferência bloqueada: o conteúdo continua selecionável.
    }
  };

  return (
    <div style={est.inspetor}>
      <header style={est.cabecalhoInspetor}>
        <span style={{ ...est.tipo, color: TOM[evento.tipo] || C.texto }}>
          {ROTULO[evento.tipo] || evento.tipo}
        </span>
        <span style={{ flex: 1, wordBreak: "break-all" }}>{evento.chave}</span>
        <button type="button" onClick={copiar} style={est.acaoInspetor}>
          {copiado ? "copiado" : "copiar"}
        </button>
        <button type="button" onClick={aoAmpliar} style={est.acaoInspetor} title="Ampliar">
          ampliar
        </button>
        <button type="button" onClick={aoFechar} style={est.acaoInspetor} title="Esc">
          &#10005;
        </button>
      </header>
      <div style={est.metaInspetor}>
        <span>{hora(evento.em)}</span>
        <span style={{ color: C.suave }}>{evento.canal}</span>
        {evento.vezes > 1 && (
          <span style={{ color: C.suave }}>
            {evento.vezes}x seguidas — mostrando a última
          </span>
        )}
      </div>
      <pre style={est.json}>{corpo}</pre>
    </div>
  );
};

/**
 * Payload em tela cheia — para quando o conteúdo não cabe no inspetor lateral
 * (mensagem com corpo grande, ticket inteiro vindo pelo socket). O lateral
 * continua sendo o caminho normal: ele deixa varrer a lista e ler ao mesmo
 * tempo, coisa que um modal por cima impede.
 */
const Ampliado = ({ evento, aoFechar }) => {
  const corpo = JSON.stringify(evento.detalhe ?? null, null, 2);
  return (
    <div
      style={est.fundoModal}
      onClick={aoFechar}
      role="presentation"
    >
      <div style={est.modal} onClick={e => e.stopPropagation()}>
        <header style={est.cabecalhoInspetor}>
          <span style={{ ...est.tipo, color: TOM[evento.tipo] || C.texto }}>
            {ROTULO[evento.tipo] || evento.tipo}
          </span>
          <span style={{ flex: 1, wordBreak: "break-all" }}>{evento.chave}</span>
          <span style={{ color: C.suave, fontVariantNumeric: "tabular-nums" }}>
            {hora(evento.em)}
          </span>
          <button type="button" onClick={aoFechar} style={est.acaoInspetor} title="Esc">
            &#10005;
          </button>
        </header>
        <pre style={{ ...est.json, flex: 1 }}>{corpo}</pre>
      </div>
    </div>
  );
};

/**
 * Painel de instrumento: base azul-marinho dessaturada, leituras em âmbar e
 * teal. Escolhido para NÃO ser o preto-com-verde-ácido de todo devtool — e
 * porque marinho frio deixa o âmbar (o custo: ida à rede) saltar sozinho.
 */
const C = {
  fundo: "#141B2D",
  painel: "#1B2337",
  rodape: "#101728",
  borda: "#2C3752",
  texto: "#C7D0E3",
  suave: "#7C89A6"
};

const FONTE = 'ui-monospace, "SF Mono", "Cascadia Mono", Consolas, monospace';

const est = {
  raiz: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2147483000,
    font: `12px/1.5 ${FONTE}`,
    color: C.texto,
    background: C.fundo,
    borderTop: `1px solid ${C.borda}`,
    boxShadow: "0 -8px 28px rgba(6,10,20,.5)"
  },
  puxador: { height: 5, cursor: "ns-resize", background: C.painel },
  corpo: { display: "flex", minHeight: 0, borderBottom: `1px solid ${C.borda}` },
  coluna: { flex: 1, minWidth: 0, overflow: "auto" },
  barra: { display: "flex", alignItems: "stretch", height: 32, background: C.rodape },

  pulso: {
    display: "flex",
    alignItems: "flex-end",
    gap: 1,
    width: 40 * 4,
    padding: "6px 10px",
    borderRight: `1px solid ${C.borda}`
  },

  aba: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "0 13px",
    background: "transparent",
    color: C.suave,
    border: "none",
    borderRight: `1px solid ${C.borda}`,
    cursor: "pointer",
    font: "inherit"
  },
  abaAtiva: { background: C.fundo, color: "#EDF1F8", boxShadow: `inset 0 2px 0 ${TOM.hit}` },
  contador: {
    background: C.painel,
    border: `1px solid ${C.borda}`,
    borderRadius: 9,
    padding: "0 6px",
    fontSize: 11,
    fontVariantNumeric: "tabular-nums"
  },
  espera: { display: "flex", alignItems: "center", padding: "0 13px", color: C.suave },

  leitura: { display: "flex", alignItems: "center", gap: 16, padding: "0 14px" },
  itemLeitura: { display: "flex", alignItems: "baseline", gap: 5 },

  acao: {
    width: 32,
    background: "transparent",
    color: C.suave,
    border: "none",
    borderLeft: `1px solid ${C.borda}`,
    cursor: "pointer",
    font: "inherit"
  },

  porChave: { borderBottom: `1px solid ${C.borda}`, padding: "6px 0" },
  linhaDominio: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    padding: "3px 14px",
    background: "transparent",
    color: C.texto,
    border: "none",
    cursor: "pointer",
    font: "inherit",
    fontVariantNumeric: "tabular-nums"
  },
  qtdChaves: { color: C.suave, minWidth: 62, textAlign: "right" },
  noArvore: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "5px 14px",
    background: "transparent",
    color: C.texto,
    border: "none",
    borderTop: `1px solid ${C.borda}`,
    cursor: "pointer",
    font: "inherit",
    fontVariantNumeric: "tabular-nums"
  },
  seta: { width: 12, color: C.suave },
  folha: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "2px 14px 2px 38px",
    background: "transparent",
    color: C.suave,
    border: "none",
    borderLeft: "2px solid transparent",
    cursor: "pointer",
    font: "inherit",
    fontVariantNumeric: "tabular-nums"
  },
  folhaAtiva: { background: C.painel, color: C.texto, borderLeft: `2px solid ${TOM.compartilhada}` },
  validade: { minWidth: 58, textAlign: "right" },

  fluxo: { padding: "4px 0" },
  linha: {
    display: "flex",
    alignItems: "baseline",
    gap: 12,
    width: "100%",
    padding: "3px 14px",
    background: "transparent",
    color: C.texto,
    border: "none",
    borderLeft: "2px solid transparent",
    textAlign: "left",
    cursor: "pointer",
    font: "inherit"
  },
  linhaAtiva: { background: C.painel, borderLeft: `2px solid ${TOM.compartilhada}` },
  horario: { color: C.suave, fontVariantNumeric: "tabular-nums" },
  tipo: { minWidth: 66 },
  chave: { flex: 1, minWidth: 0, wordBreak: "break-all" },
  dica: { color: C.suave },
  vezes: {
    background: C.painel,
    border: `1px solid ${C.borda}`,
    borderRadius: 9,
    padding: "0 6px",
    color: C.suave,
    fontVariantNumeric: "tabular-nums"
  },

  inspetor: {
    width: "40%",
    minWidth: 260,
    display: "flex",
    flexDirection: "column",
    borderLeft: `1px solid ${C.borda}`,
    background: C.painel
  },
  cabecalhoInspetor: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 12px",
    borderBottom: `1px solid ${C.borda}`
  },
  acaoInspetor: {
    background: "transparent",
    color: C.suave,
    border: `1px solid ${C.borda}`,
    borderRadius: 3,
    padding: "1px 7px",
    cursor: "pointer",
    font: "inherit"
  },
  metaInspetor: {
    display: "flex",
    gap: 12,
    padding: "5px 12px",
    color: C.texto,
    borderBottom: `1px solid ${C.borda}`,
    fontVariantNumeric: "tabular-nums"
  },
  json: { margin: 0, padding: 12, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" },

  vazio: { margin: 0, padding: 16, color: C.suave },

  fundoModal: {
    position: "fixed",
    inset: 0,
    background: "rgba(8,12,22,.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 40
  },
  modal: {
    display: "flex",
    flexDirection: "column",
    width: "min(900px, 100%)",
    maxHeight: "100%",
    background: C.painel,
    border: `1px solid ${C.borda}`,
    boxShadow: "0 24px 60px rgba(6,10,20,.6)"
  }
};

export default Devtools;
